import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { GradientButton } from '../../components/common/Primitives';
import { RESEARCHER_COLLABORATE } from '../../lib/mockResearcher';
import { submitForm } from '../../lib/api';
import { CheckCircle2, Check, ArrowRight } from 'lucide-react';

const inputCls = 'w-full rounded-lg border border-canvas-200 bg-white px-4 py-2.5 text-[14px] text-navy placeholder:text-ink-500/60 focus:outline-none focus:ring-2 focus:ring-purple/40';
const labelCls = 'text-[13px] font-semibold text-ink-700';

export default function ResearcherCollaborate() {
  const d = RESEARCHER_COLLABORATE;
  const [form, setForm] = useState({
    name: '', role: '', institution: '', department: '', country: '',
    email: '', linkedin: '',
    interests: [], question: '', method: '', timeline: '', notes: '',
    readiness: [false, false, false],
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleInterest = (i) => setForm(p => ({ ...p, interests: p.interests.includes(i) ? p.interests.filter(x => x !== i) : [...p.interests, i] }));
  const toggleReady = (i) => setForm(p => ({ ...p, readiness: p.readiness.map((c, ci) => ci === i ? !c : c) }));
  const allReady = form.readiness.every(Boolean);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allReady || submitting) return;
    setSubmitting(true); setSubmitError(null);
    try {
      await submitForm({
        formType: 'researcher-collaborate',
        fields: form,
        contactEmail: form.email,
        contactName: form.name,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PageShell>
        <section className="px-6 py-24" data-testid="researcher-collab-confirmation">
          <div className="mx-auto max-w-[720px]">
            <div className="rounded-3xl border border-mint/30 bg-gradient-to-br from-mint-50 via-cyan-50 to-purple-50 p-10 shadow-soft text-center">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-mint text-white shadow"><CheckCircle2 className="h-8 w-8" /></div>
              <h2 className="mt-5 text-[26px] md:text-[30px] font-extrabold text-navy">{d.confirmation.title}</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-700">{d.confirmation.body}</p>
              <div className="mt-6"><GradientButton to={d.confirmation.cta.to}>{d.confirmation.cta.label}</GradientButton></div>
            </div>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="researcher-collab-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold tracking-widest text-purple">{d.hero.eyebrow}</div>
          <div className="mt-4 grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <h1 className="text-[40px] md:text-[50px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-canvas-100 p-5 shadow-soft">
              <div className="text-[13px] font-bold text-navy">{d.hero.tipsTitle}</div>
              <ul className="mt-3 space-y-2">
                {d.hero.tips.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-purple" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="px-6 py-8" data-testid="collab-form-section">
        <div className="mx-auto max-w-[960px]">
          <form onSubmit={onSubmit} className="rounded-3xl border border-canvas-200 bg-white p-6 md:p-10 shadow-soft space-y-6" data-testid="collab-form">
            <div>
              <h2 className="text-[20px] font-extrabold text-navy">{d.formTitle}</h2>
              <p className="mt-1 text-[12.5px] text-ink-500">{d.formHint}</p>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="r-name">Name</label>
                <input id="r-name" required data-testid="collab-name" value={form.name} onChange={e => set('name', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Full name" />
              </div>
              <div>
                <label className={labelCls} htmlFor="r-role">Role or designation</label>
                <input id="r-role" required data-testid="collab-role" value={form.role} onChange={e => set('role', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="e.g. Faculty, PhD scholar" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="r-inst">University or institution</label>
                <input id="r-inst" required data-testid="collab-inst" value={form.institution} onChange={e => set('institution', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter institution" />
              </div>
              <div>
                <label className={labelCls} htmlFor="r-dept">Department</label>
                <input id="r-dept" data-testid="collab-dept" value={form.department} onChange={e => set('department', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="e.g. Learning sciences" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="r-country">Country</label>
                <input id="r-country" data-testid="collab-country" value={form.country} onChange={e => set('country', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Country" />
              </div>
              <div>
                <label className={labelCls} htmlFor="r-email">Email</label>
                <input id="r-email" type="email" required data-testid="collab-email" value={form.email} onChange={e => set('email', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="name@university.edu" />
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="r-linkedin">LinkedIn / research profile</label>
              <input id="r-linkedin" type="url" data-testid="collab-linkedin" value={form.linkedin} onChange={e => set('linkedin', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="https://..." />
            </div>

            {/* Interests */}
            <div>
              <div className={labelCls}>Areas of interest (select any)</div>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {d.interests.map(int => {
                  const on = form.interests.includes(int);
                  return (
                    <label key={int} className={`cursor-pointer rounded-xl border px-4 py-3 text-[13.5px] font-semibold shadow-soft transition-colors ${on ? 'border-purple bg-purple-50 text-purple' : 'border-canvas-200 bg-white text-navy hover:border-purple/40'}`} data-testid={`collab-interest-${int.split(' ')[0].toLowerCase()}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleInterest(int)} className="sr-only" />
                      {int}
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className={labelCls} htmlFor="r-q">Research question</label>
              <textarea id="r-q" required rows={3} data-testid="collab-question" value={form.question} onChange={e => set('question', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Describe the research question and its relevance to immersive learning" />
            </div>
            <div>
              <label className={labelCls} htmlFor="r-method">Proposed method or expertise</label>
              <textarea id="r-method" rows={3} data-testid="collab-method" value={form.method} onChange={e => set('method', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Method, expertise or contribution" />
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="r-time">Indicative timeline</label>
                <input id="r-time" data-testid="collab-timeline" value={form.timeline} onChange={e => set('timeline', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="e.g. Aug 2026 – Feb 2027" />
              </div>
              <div>
                <label className={labelCls} htmlFor="r-notes">Additional notes</label>
                <input id="r-notes" data-testid="collab-notes" value={form.notes} onChange={e => set('notes', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Anything else the team should know" />
              </div>
            </div>

            {/* Readiness */}
            <div>
              <div className={labelCls}>Readiness confirmation</div>
              <div className="mt-3 space-y-2.5">
                {d.readiness.map((r, i) => (
                  <label key={i} className={`flex items-start gap-3 rounded-xl border p-4 shadow-soft cursor-pointer transition-colors ${form.readiness[i] ? 'border-mint bg-mint-50' : 'border-canvas-200 bg-white hover:border-purple/40'}`} data-testid={`collab-ready-${i}`}>
                    <input type="checkbox" checked={form.readiness[i]} onChange={() => toggleReady(i)} className="mt-1 h-4 w-4 rounded border-canvas-200 text-purple focus:ring-purple/40" />
                    <span className="text-[13.5px] text-ink-700">{r}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-canvas-200">
              <button type="submit" disabled={!allReady || submitting} data-testid="collab-submit" className={`inline-flex items-center gap-1 rounded-lg px-6 py-3 text-[14px] font-semibold text-white shadow-soft ${allReady && !submitting ? 'bg-brand-gradient hover:brightness-110' : 'bg-canvas-200 text-ink-500 cursor-not-allowed'}`}>{submitting ? 'Submitting…' : d.submit} <ArrowRight className="h-4 w-4" /></button>
              <div className="text-[12px] text-ink-500">
                <div>{d.submitNote}</div>
                <div className="mt-0.5 italic">{d.privacyNote}</div>
              </div>
            </div>
            {submitError && (
              <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[12.5px] text-red-700" data-testid="collab-submit-error">{submitError}</div>
            )}
          </form>
        </div>
      </section>

      {/* NEXT STEPS + BEFORE SUBMITTING */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px] grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <h3 className="text-[20px] font-extrabold text-navy">{d.nextTitle}</h3>
            <ol className="mt-4 space-y-3">
              {d.next.map((n) => (
                <li key={n.n} className="rounded-2xl border border-canvas-200 bg-white p-4 shadow-soft flex gap-3">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-purple text-white text-[13px] font-bold flex-none">{n.n}</span>
                  <div>
                    <div className="text-[14px] font-bold text-navy">{n.title}</div>
                    <div className="text-[12.5px] text-ink-500">{n.desc}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <h3 className="text-[20px] font-extrabold text-navy">{d.beforeTitle}</h3>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {d.beforeItems.map((b, i) => (
                <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-4 shadow-soft">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-royal-50 text-royal text-[12px] font-extrabold">{b.key}</div>
                  <div className="mt-2 text-[14px] font-bold text-navy">{b.title}</div>
                  <div className="text-[12.5px] text-ink-500">{b.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
